import { getMainFillJustification } from '@miles/domain/Dashboard/Application/FillJustification/fillJustifications';
import { match, P } from 'ts-pattern';
import { getAgeFromBirthDate } from '../../../Utils/Date';
import { Gender } from '../../../Utils/Gender';
import { z } from 'zod';
export class FormFunnelConfigConditionService {
    constructor() {
        this.shouldDisplayFormToAttendee = (allConditions, attendee) => {
            if (!Array.isArray(allConditions)) {
                return false;
            }
            // at least one of allConditions must be respected
            return allConditions.some((condition) => Array.isArray(condition)
                ? // a condition is a conjunction of sub-conditions, so all of them must be met
                    condition.every((subCondition) => this.getComparisonMethod(subCondition)(attendee))
                : false);
        };
        this.getComparisonMethod = (subCondition) => match(FormFunnelConfigSchema.parse(subCondition))
            .with({ keyName: 'age' }, this.compareAge)
            .with({ keyName: 'license' }, this.checkLicense)
            .with({ keyName: 'isCaptain' }, this.isCaptain)
            .with({ keyName: 'gender' }, this.checkGender)
            .with({ keyName: 'group' }, this.checkGroup)
            .exhaustive();
        this.isCaptain = () => (attendee) => attendee.isCaptain;
        this.compareAge = ({ operator, value }) => (attendee) => {
            const attendeeAge = attendee.age;
            if (!attendeeAge)
                return false;
            return match(operator)
                .with(ComparisonOperator.EQUALS, () => attendeeAge === value)
                .with(P.union(ComparisonOperator.LOWER_THAN, ComparisonOperator.LTE), () => attendeeAge <= value)
                .with(P.union(ComparisonOperator.GREATER_THAN, ComparisonOperator.GTE), () => attendeeAge >= value)
                .exhaustive();
        };
        this.checkLicense = (config) => (attendee) => attendee.isLicensed === config.value;
        this.checkGender = (config) => (attendee) => attendee.gender === config.value;
        this.checkGroup = ({ groupIds, include }) => (attendee) => attendee.groupId
            ? include
                ? groupIds.includes(attendee.groupId)
                : !groupIds.includes(attendee.groupId)
            : !include;
    }
}
export const liveFormFunnelConfigConditionService = new FormFunnelConfigConditionService();
export const fromGetAttendeeFromIdToAttendeeData = (attendee) => {
    var _a, _b;
    const isLicensed = match(attendee)
        .with({
        licenseNumber: P.when((licenseNumber) => licenseNumber &&
            (!licenseNumber.toUpperCase().startsWith('P') ||
                licenseNumber.length < 11))
    }, () => true)
        .with({ licenseNumber: P.nullish }, P.when(() => !!getMainFillJustification(attendee.fillJustifications)), () => false)
        .otherwise(() => undefined);
    return {
        age: attendee.birthDate
            ? getAgeFromBirthDate(attendee.birthDate.toISOString())
            : undefined,
        gender: (_a = attendee.gender) !== null && _a !== void 0 ? _a : undefined,
        isCaptain: !!attendee.teamRegistration,
        isLicensed,
        groupId: (_b = attendee.groupId) !== null && _b !== void 0 ? _b : undefined
    };
};
export var ComparisonOperator;
(function (ComparisonOperator) {
    ComparisonOperator["EQUALS"] = "equals";
    /**
     *  @deprecated Use `LTE` instead
     */
    ComparisonOperator["LOWER_THAN"] = "lowerthan";
    /**
     * @deprecated Use `GTE` instead
     */
    ComparisonOperator["GREATER_THAN"] = "greaterthan";
    ComparisonOperator["LTE"] = "lte";
    ComparisonOperator["GTE"] = "gte";
})(ComparisonOperator || (ComparisonOperator = {}));
export const comparisonOperatorWithoutDeprecated = Object.values(ComparisonOperator).filter((operator) => !['lowerthan', 'greaterthan'].includes(operator));
const FormFunnelConfigCaptainSchema = z.object({
    keyName: z.literal('isCaptain')
});
const FormFunnelConfigGenderSchema = z.object({
    keyName: z.literal('gender'),
    value: z.nativeEnum(Gender)
});
const FormFunnelConfigAgeSchema = z.object({
    keyName: z.literal('age'),
    operator: z.nativeEnum(ComparisonOperator),
    value: z.number()
});
const FormFunnelConfigLicenseSchema = z.object({
    keyName: z.literal('license'),
    value: z.boolean()
});
const FormFunnelConfigGroupSchema = z.object({
    keyName: z.literal('group'),
    groupIds: z.array(z.number()).nonempty(),
    include: z.boolean().default(true)
});
export const FormFunnelConfigSchema = z.discriminatedUnion('keyName', [
    FormFunnelConfigCaptainSchema,
    FormFunnelConfigAgeSchema,
    FormFunnelConfigLicenseSchema,
    FormFunnelConfigGenderSchema,
    FormFunnelConfigGroupSchema
]);
export const FormFunnelConfigConditionKeyNames = FormFunnelConfigSchema.options.map(({ shape: { keyName: { value } } }) => value);
